/**
 * Systems (docs/systems.md "Systems view"): the tenant as systems and the integrations between them, beside Now. A
 * headline in Now's voice ("14 systems · 17 integrations", what is failing and overdue), what needs attention (a compact
 * list by default, cards, or folded to one line: `att=`, a history step, remembered in the browser; one click
 * to the integration), the live system map (systems as nodes, integrations as arrows: thicker = more runs in the range,
 * failing red, overdue dashed), and every integration as a compact list. The range (1 h · 24 h · 7 d), the sort and the
 * selected arrow are in the URL, each change a history step (Back undoes it); a system or an integration opens its page as a history step.
 * Whether an integration is failing or overdue is judged over seven days whatever the range; the range sets the runs.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { Api, type Integration } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useEnv } from '../lib/envs.tsx';
import { useSearchParams } from '../lib/liveparams.ts';
import { rememberSearch } from '../lib/hooks.ts';
import { count, duration } from '../lib/format.ts';
import { cadenceLabel } from '../lib/integrations.ts';
import {
  ATTENTION_VIEWS, oneRowPerProcess, SORTS, SORT_LABEL, attentionOf, attentionSummary, readAttentionView, storedAttentionView, withAttentionView, headlineOf, integrationKey, integrationPath, integrationQuery, rangeFrom, rangeWords, readRange, readSort,
  sortIntegrations, systemPath, withRange, withSort, withWindow,
} from '../lib/systemsview.ts';
import { stripSystem } from '../lib/systems.ts';
import { Shell } from '../shell/Shell.tsx';
import { Num, Pill, Surface, cx } from '../design/primitives.tsx';
import { SystemMap } from '../components/SystemMap.tsx';
import { useClock } from '../now/useLive.ts';
import { useRangeLive } from '../systems/useRangeLive.ts';
import { Chain, IntegrationRow, Pair, RangePills, Section, StatusMark, NameToggle } from '../systems/parts.tsx';
import { GroupFilter } from '../components/GroupFilter.tsx';
import { filterIntegrations, hiddenSystems, readHidden } from '../lib/groups.ts';
import { useSystems } from '../lib/systemsdata.tsx';

const POLL_MS = 60_000;
const WEEK = 'now-7d';

export function SystemsView() {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const navigate = useNavigate();
  const { link, current } = useEnv();
  const [params, setParams] = useSearchParams();
  const range = readRange(params);
  const sort = readSort(params);
  const arc = params.get('arc');
  const [pref] = useState(storedAttentionView);
  const attView = readAttentionView(params, pref);
  const now = useClock(30_000);

  const live = useRangeLive((r) => Promise.all([
    Api.integrations(tid, new URLSearchParams({ from: WEEK })),
    r === '7d' ? Promise.resolve(null) : Api.integrations(tid, new URLSearchParams({ from: rangeFrom(r) })),
  ]).then(([week, ranged]) => ({ week, ranged })), range, POLL_MS, [tid, current?.id]);

  const data = live.data;
  // The groups of systems hidden (`hide=`): their integrations are left out of the counts, the map and the list
  const { systems } = useSystems();
  const hiddenKey = readHidden(params).join('|');
  const hidden = useMemo(() => hiddenSystems(systems.values(), hiddenKey ? hiddenKey.split('|') : []), [systems, hiddenKey]);
  const list = useMemo(() => (data ? filterIntegrations(withWindow(data.week.integrations, data.ranged?.integrations ?? null), hidden) : []), [data, hidden]);
  const head = useMemo(() => headlineOf(list), [list]);
  // The lists have a row per integration (a process through several systems is one); the map a line per hop
  const rows = useMemo(() => oneRowPerProcess(list), [list]);
  const attention = useMemo(() => attentionOf(rows), [rows]);
  const sorted = useMemo(() => sortIntegrations(rows, sort), [rows, sort]);
  const shownRange = data?.range ?? range;
  const partial = !!data && (data.week.partial || !!data.ranged?.partial);

  const go = (q: string) => {
    if (!q) return;
    rememberSearch(q);
    void navigate(link(`/s?${new URLSearchParams({ q })}`));
  };
  // Switching how "Needs attention" is shown is a history step (Back returns to the form before). The entry left keeps
  // its own form in its URL first, so Back shows it and not the remembered newest choice.
  const showAttention = (v: typeof attView) => {
    if (v === attView) return;
    const here = new URL(window.location.href);
    if (here.searchParams.get('att') !== attView) {
      here.searchParams.set('att', attView);
      window.history.replaceState(window.history.state, '', here.pathname + here.search + here.hash);
    }
    setParams((p) => withAttentionView(p, v));
  };

  return (
    <Shell search={{ value: '', onSubmit: go, size: 'compact' }}>
      <div className="sys-page" data-systems-view="">
        <header className="sys-head">
          <div className="min-w-0">
            {data ? (
              <>
                <h1 className="sys-sentence m-0 font-normal">
                  <Num size="display" value={head.systems} />
                  <span className="text-title font-medium text-n9">{head.systems === 1 ? 'system' : 'systems'} · {count(head.integrations)} {head.integrations === 1 ? 'integration' : 'integrations'}</span>
                </h1>
                {head.failing + head.overdue > 0 ? (
                  <p className="m-0 mt-1 text-body text-n9 inline-flex items-center gap-x-4 gap-y-1 flex-wrap" data-headline-status="">
                    {head.failing > 0 && <span className="inline-flex items-center gap-2"><i className="size-2 rounded-full bg-fail shrink-0" aria-hidden="true" />{count(head.failing)} failing</span>}
                    {head.overdue > 0 && <span className="inline-flex items-center gap-2"><i className="size-2 rounded-full bg-warn shrink-0" aria-hidden="true" />{count(head.overdue)} overdue</span>}
                  </p>
                ) : <p className="m-0 mt-1 text-body text-n8">{head.integrations ? 'All integrations running' : 'No integrations seen in the last 7 days'}</p>}
              </>
            ) : live.error ? (
              <p className="m-0 text-body text-fail">{live.error}</p>
            ) : (
              <div aria-busy="true" className="flex flex-col gap-3">
                <span className="mv-shimmer rounded-chip inline-block h-[50px] w-[420px] max-w-full" />
                <span className="mv-shimmer rounded-chip inline-block h-[18px] w-[200px]" />
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 font-mono text-micro text-n7">
              <i className={cx('size-1.5 rounded-full shrink-0', live.refreshing || live.loading ? 'bg-n5' : 'bg-ok now-live')} aria-hidden="true" />
              {dayjs(now).format('HH:mm')}
            </span>
            <GroupFilter />
            <NameToggle />
            <RangePills value={range} onChange={(r) => setParams((p) => withRange(p, r))} />
          </div>
        </header>

        {data && attention.length > 0 && (
          <Section title="Needs attention" fact={attView === 'hidden' ? attentionSummary(attention) : count(attention.length)} className={attView === 'hidden' ? 'mt-6' : 'mt-8'} id="sys-attention"
            action={(
              <div role="group" aria-label="Show needs attention as" className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-n3" data-attention-view="">
                {ATTENTION_VIEWS.map((v) => <Pill key={v} active={v === attView} onClick={() => showAttention(v)}>{v === 'list' ? 'List' : v === 'cards' ? 'Cards' : 'Hide'}</Pill>)}
              </div>
            )}>
            {attView === 'cards' && (
              <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]" data-attention="cards">
                {attention.map((i) => <AttentionCard key={integrationKey(i)} i={i} now={now} rangeLabel={rangeWords(shownRange)} />)}
              </div>
            )}
            {attView === 'list' && (
              <div className="grid gap-x-2 gap-y-1 grid-cols-[repeat(auto-fill,minmax(440px,1fr))]" data-attention="list">
                {attention.map((i) => <AttentionLine key={integrationKey(i)} i={i} now={now} rangeLabel={rangeWords(shownRange)} />)}
              </div>
            )}
          </Section>
        )}

        <Section title="System map" fact={rangeWords(shownRange)} className="mt-8" id="sys-map">
          {data ? (
            <SystemMap integrations={list} selected={arc} windowLabel={rangeWords(shownRange)} openWhat="its page" bleed
              onSelect={(a) => setParams((p) => { const n = new URLSearchParams(p); if (a) n.set('arc', a); else n.delete('arc'); return n; })}
              onOpenSystem={(code) => void navigate(link(systemPath(code)))}
              onOpenProcess={(process) => void navigate(link(integrationPath(process)))}
              onSearch={(process) => go(integrationQuery(process))} />
          ) : <Surface className="h-[420px] mv-shimmer" aria-hidden="true" />}
        </Section>

        {data && list.length > 0 && (
          <Section title="All integrations" fact={count(rows.length)} className="mt-10" id="sys-all"
            action={(
              <div role="group" aria-label="Sort" className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-n3">
                {SORTS.map((s) => <Pill key={s} active={s === sort} onClick={() => setParams((p) => withSort(p, s))}>{SORT_LABEL[s]}</Pill>)}
              </div>
            )}>
            <Surface className="p-1.5">
              <div className="sys-rows" data-integration-list="">
                {sorted.map((i) => <IntegrationRow key={integrationKey(i)} i={i} now={now} />)}
              </div>
            </Surface>
            <p className="m-0 text-micro text-n7">Runs in the {rangeWords(shownRange)}; failing and overdue are judged over the last 7 days. An integration is a process carrying data from one system to another.</p>
          </Section>
        )}
        {partial && <p className="m-0 mt-4 text-micro text-warn">Some vaults did not answer; what is shown may be incomplete.</p>}
      </div>
    </Shell>
  );
}

/** What is wrong with a failing or overdue integration, in a few words. */
function attentionReason(i: Integration, now: number, rangeLabel: string, short = false) {
  const cadence = cadenceLabel(i.cadenceMs);
  if (i.status === 'failing') {
    if (short) return i.runs > 0 ? `${count(i.failed)} of ${count(i.runs)} failed` : 'newest run failed';
    return i.runs > 0 ? `The newest run failed · ${count(i.failed)} of ${count(i.runs)} ${i.runs === 1 ? 'run' : 'runs'} in the ${rangeLabel}` : `The newest run failed · no runs in the ${rangeLabel}`;
  }
  const silent = i.lastRun ? duration(Math.max(60_000, now - i.lastRun)) : 'a long time';
  return short ? `silent ${silent}${cadence ? `, usually ${cadence}` : ''}` : `Silent for ${silent}${cadence ? `, usually ${cadence}` : ''}`;
}

/** The compact form: one line per integration — status, name, where data goes, what is wrong. */
function AttentionLine({ i, now, rangeLabel }: { i: Integration; now: number; rangeLabel: string }) {
  const { link } = useEnv();
  const failing = i.status === 'failing';
  return (
    <Link to={link(integrationPath(i.process))} data-attention-item={i.process} title={`${i.process} — ${attentionReason(i, now, rangeLabel)}`}
      className={cx('flex items-center gap-3 h-10 pl-3 pr-3 rounded-chip no-underline text-n10 text-small min-w-0 transition-colors duration-[var(--t-micro)] border-l-[3px]',
        failing ? 'bg-fail-soft/70 border-l-fail hover:bg-fail-soft' : 'bg-n2 border-l-warn hover:bg-n3')}>
      <span className="font-semibold truncate min-w-0">{stripSystem(i.process)}</span>
      <span className={cx('text-micro whitespace-nowrap truncate', failing ? 'text-fail' : 'text-warn')}>{attentionReason(i, now, rangeLabel, true)}</span>
      <span className="ml-auto shrink-0">{i.path && i.path.length > 2 ? <Chain path={i.path} /> : <Pair from={i.from} to={i.to} />}</span>
    </Link>
  );
}

/** A failing or overdue integration: what is wrong in a few words, one click to its page. */
function AttentionCard({ i, now, rangeLabel }: { i: Integration; now: number; rangeLabel: string }) {
  const { link } = useEnv();
  const failing = i.status === 'failing';
  return (
    <Link to={link(integrationPath(i.process))} data-attention-item={i.process}
      className={cx('flex flex-col gap-2.5 p-4 rounded-card no-underline text-n10 transition-[box-shadow,background] duration-[var(--t-micro)] min-w-0',
        failing ? 'bg-fail-soft ring-1 ring-inset ring-fail/30 hover:ring-fail/60' : 'bg-n2 ring-1 ring-inset ring-n4 hover:ring-n6')}>
      <span className="flex items-center gap-3 min-w-0">
        <StatusMark status={i.status} />
        <span className="ml-auto">{i.path && i.path.length > 2 ? <Chain path={i.path} /> : <Pair from={i.from} to={i.to} />}</span>
      </span>
      <span className="text-body font-semibold truncate" title={i.process}>{stripSystem(i.process)}</span>
      <span className="text-small text-n8">{attentionReason(i, now, rangeLabel)}</span>
    </Link>
  );
}

